import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/shared/api/client';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskPhone } from '@/shared/lib/input-masks';
import { onlyDigits } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import type { PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import { GENDER_OPTIONS } from '@/features/public-invite/model/invite-form-schema';
import { isValidEmail } from '@/features/nps/public-survey/partition-questions';
import { PublicInviteCustomFields } from '@/features/public-invite/ui/public-invite-custom-fields';
import { PublicInviteStateCityFields } from '@/features/public-invite/ui/public-invite-state-city-fields';
import {
  participantApi,
  type NotificationChannels,
  type ParticipantProfile,
} from '../../api/participant-api';
import { maskIdentity, participantFormPlan } from '../../model/form-plan';

interface ProfileTabProps {
  idPassbook: string;
  uniqueId: string;
  profile: ParticipantProfile;
  /** Config pública da loja (a mesma do cadastro): decide quais campos aparecem. */
  passbook: PublicInvitePassbook;
  accentColor?: string;
  foregroundColor?: string;
  onSaved: (profile: ParticipantProfile) => void;
  /** Avisa a página se há alteração não salva (troca de aba pede confirmação). */
  onDirtyChange: (dirty: boolean) => void;
}

/** Canais que a pessoa controla aqui; `webpush` segue como está (não há web push nesta página). */
const CHANNELS: { key: keyof NotificationChannels; label: string; hint: string }[] = [
  { key: 'whatsapp', label: 'WhatsApp', hint: 'Promoções e avisos do programa' },
  { key: 'email', label: 'E-mail', hint: 'Novidades e seu cartão' },
  { key: 'sms', label: 'SMS', hint: 'Mensagens de texto' },
  { key: 'push', label: 'Notificações no celular', hint: 'Avisos do cartão na carteira do celular' },
];

/** O legado grava `dd/mm/aaaa`, mas há cadastro antigo em `aaaa-mm-dd`. */
function normalizeBirthday(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return /^\d{2}\/\d{2}\/\d{4}$/.test(value) ? value : '';
}

function toForm(profile: ParticipantProfile): ParticipantProfile {
  return {
    ...profile,
    phone: maskPhone(profile.phone),
    birthday: normalizeBirthday(profile.birthday),
    notifications: { ...profile.notifications },
    fields: { ...profile.fields },
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

/**
 * Meus dados com os MESMOS campos que o cadastro daquela loja coletou
 * (`resolveInviteFieldPlan`): nascimento/gênero, estado/cidade, campos extras
 * (`passbook.fields`). O BFF valida de novo e preserva o resto do cadastro.
 */
export function ProfileTab({
  idPassbook,
  uniqueId,
  profile,
  passbook,
  accentColor,
  foregroundColor,
  onSaved,
  onDirtyChange,
}: ProfileTabProps) {
  const formPlan = useMemo(() => participantFormPlan(passbook), [passbook]);
  const { plan, customFields, editableSlugs, childSuffix } = formPlan;
  const saved = useMemo(() => toForm(profile), [profile]);
  const [form, setForm] = useState<ParticipantProfile>(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Só o que mudou: o BFF parte do cadastro atual e preserva o resto. */
  const patch = useMemo(() => {
    const out: Partial<ParticipantProfile> = {};
    if (form.name.trim() !== saved.name.trim()) out.name = form.name.trim().replace(/\s+/g, ' ');
    if (form.email.trim().toLowerCase() !== saved.email.trim().toLowerCase()) out.email = form.email.trim();
    if (onlyDigits(form.phone) !== onlyDigits(saved.phone)) out.phone = onlyDigits(form.phone);
    if (plan.showGender && form.gender !== saved.gender) out.gender = form.gender;
    if (CHANNELS.some(({ key }) => form.notifications[key] !== saved.notifications[key])) {
      out.notifications = form.notifications;
    }
    const fields: Record<string, string> = {};
    for (const slug of editableSlugs) {
      if ((form.fields[slug] ?? '') !== (saved.fields[slug] ?? '')) fields[slug] = form.fields[slug] ?? '';
    }
    if (Object.keys(fields).length > 0) out.fields = fields;
    return out;
  }, [form, saved, plan, editableSlugs]);

  const dirty = Object.keys(patch).length > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Fechar/recarregar a aba do navegador com alteração pendente: o navegador pergunta.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const set = useCallback(
    <K extends keyof ParticipantProfile>(key: K, value: ParticipantProfile[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const setField = useCallback(
    (slug: string, value: string) => setForm((prev) => ({ ...prev, fields: { ...prev.fields, [slug]: value } })),
    [],
  );

  /** Valida só o que a pessoa mexeu: cadastro antigo incompleto não trava as outras mudanças. */
  const validate = (): string | null => {
    if (patch.name !== undefined && patch.name.split(' ').length < 2) return 'Informe nome e sobrenome.';
    if (patch.email !== undefined && !isValidEmail(patch.email)) return 'Informe um e-mail válido.';
    if (patch.phone !== undefined && patch.phone.length !== 11) return 'Informe o celular com DDD.';
    if (patch.fields) {
      if (plan.showStateCity && ('state' in patch.fields || 'city' in patch.fields)) {
        if (!form.fields.state) return 'Selecione o estado.';
        if (!form.fields.city) return 'Selecione a cidade.';
      }
      for (const field of customFields) {
        if (field.slug in patch.fields && field.required && !field.highlight && !patch.fields[field.slug]?.trim()) {
          return `Preencha o campo: ${field.label.replace(/\s*\*\s*$/, '')}.`;
        }
      }
    }
    return null;
  };

  const accentButton: CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
    : undefined;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const invalid = validate();
    setError(invalid);
    if (invalid || !dirty) return;
    setSaving(true);
    try {
      const result = await participantApi.saveProfile(idPassbook, patch);
      onSaved(result.profile);
      setForm(toForm(result.profile));
      toast.success('Cadastro atualizado com sucesso.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sem conexão. Confira sua internet e tente de novo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="public-invite-accent-scope mx-auto w-full max-w-xl"
      style={accentColor ? ({ '--invite-accent': accentColor } as CSSProperties) : undefined}
    >
      <form onSubmit={onSubmit} noValidate>
        <CardHeader>
          <CardTitle>Meus dados</CardTitle>
          <CardDescription>Mantenha seus dados em dia para receber seus benefícios.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-7">
          <Section title="Dados pessoais">
            <div className="space-y-2">
              <Label htmlFor="profile-identity">
                {formPlan.identityLabel}
                {childSuffix}
              </Label>
              <Input id="profile-identity" value={maskIdentity(formPlan.identity, uniqueId)} readOnly disabled />
              <p className="text-xs text-muted-foreground">
                Identifica seu cadastro e não pode ser alterado aqui.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-name">Nome completo</Label>
              <Input
                id="profile-name"
                autoComplete="name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="profile-email">E-mail</Label>
                <Input
                  id="profile-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-phone">Celular</Label>
                <MaskedInput
                  id="profile-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={form.phone}
                  onValueChange={(v) => set('phone', v)}
                  mask={maskPhone}
                />
                {patch.phone !== undefined ? (
                  <p className="text-xs text-muted-foreground">É para esse número que vai o código de acesso.</p>
                ) : null}
              </div>
            </div>
            {plan.showBirthday || plan.showGender ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {plan.showBirthday ? (
                  <div className="space-y-2">
                    <Label htmlFor="profile-birthday">Data de nascimento{childSuffix}</Label>
                    <Input id="profile-birthday" value={form.birthday} readOnly disabled />
                    <p className="text-xs text-muted-foreground">Não pode ser alterada aqui.</p>
                  </div>
                ) : null}
                {plan.showGender ? (
                  <div className="space-y-2">
                    <Label>Gênero{childSuffix}</Label>
                    <Select value={form.gender || undefined} onValueChange={(v) => set('gender', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Section>

          {plan.showStateCity ? (
            <Section title="Onde você está">
              <PublicInviteStateCityFields
                idPassbook={idPassbook}
                state={form.fields.state ?? ''}
                city={form.fields.city ?? ''}
                onStateChange={(v) => setField('state', v)}
                onCityChange={(v) => setField('city', v)}
              />
            </Section>
          ) : null}

          {customFields.length > 0 ? (
            <Section title="Informações adicionais">
              <PublicInviteCustomFields fields={customFields} values={form.fields} onChange={setField} />
            </Section>
          ) : null}

          <Section title="Como podemos falar com você">
            <div className="space-y-3 rounded-xl border border-border/70 p-4">
              {CHANNELS.map(({ key, label, hint }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <Label htmlFor={`profile-channel-${key}`} className="cursor-pointer space-y-0.5">
                    <span className="block text-sm">{label}</span>
                    <span className="block text-xs font-normal text-muted-foreground">{hint}</span>
                  </Label>
                  <Switch
                    id={`profile-channel-${key}`}
                    checked={form.notifications[key]}
                    onCheckedChange={(checked) => set('notifications', { ...form.notifications, [key]: checked })}
                    style={form.notifications[key] && accentColor ? { backgroundColor: accentColor } : undefined}
                  />
                </div>
              ))}
            </div>
          </Section>

          {error ? (
            <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="sm:flex-1"
              disabled={!dirty || saving}
              onClick={() => {
                setError(null);
                setForm(saved);
              }}
            >
              Desfazer
            </Button>
            <Button
              type="submit"
              size="lg"
              className={cn('sm:flex-[2]', accentButton && 'hover:opacity-90')}
              style={accentButton}
              loading={saving}
              disabled={!dirty}
            >
              Salvar alterações
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
}
